import React from "react";
import { Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfiniteScroll from "react-infinite-scroll-component";
import CommonDialog from "../../commondialog/confirm";
import RelanceCredit from "../../components/CreditEchec";
import IntegriteBadges from "../../components/IntegriteBadges";
import TroisBarres, { Niveau } from "../../components/charte/TroisBarres";
import { BoutonPrincipal, BoutonSecondaire } from "../../components/charte/Boutons";
import { COULEURS } from "../../theme/tribeTheme";
import { FicheLieu } from "./logiqueLieux";

/* Éléments de présentation partagés par la carte des lieux, la file de
   validation et l'Accueil. Aucune logique de décision ici : les actions
   viennent de useFicheLieu / useFileLieux (logiqueLieux.ts). */

const pastille = (couleur: string): React.CSSProperties => ({
  display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 6,
  fontSize: 11, fontWeight: 600, color: couleur, border: `1px solid ${couleur}`, background: "transparent",
  whiteSpace: "nowrap",
});

// Écart entre le GPS EXIF de la photo et la position déclarée ; la couleur
// suit le seuil configuré côté serveur (gpsConforme), pas un seuil en dur.
export function ProximityBadge({ distance, conforme }: { distance?: number | null; conforme?: boolean | null }) {
  const couleur = distance == null ? COULEURS.texteSecondaire : conforme === false ? COULEURS.erreur : COULEURS.succes;
  const label = distance == null ? "GPS photo absent" : `Photo à ${Math.round(distance)} m`;
  return (
    <span title={distance == null ? "Pas de position GPS dans la photo" : "Écart GPS photo / position déclarée"} style={pastille(couleur)}>
      {label}
    </span>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { color: string; label: string }> = {
    approved: { color: COULEURS.succes, label: "Approuvé" },
    rejected: { color: COULEURS.erreur, label: "Rejeté" },
  };
  const s = map[(status ?? "").toLowerCase()] ?? { color: COULEURS.attention, label: "En attente" };
  return <span style={pastille(s.color)}>{s.label}</span>;
}

export function CorroborationBadge({ nb }: { nb: number }) {
  return nb > 0
    ? <span style={pastille(COULEURS.info)} title="Autres Spotters à moins de 20 m">{nb + 1} Spotters</span>
    : <span style={pastille(COULEURS.texteSecondaire)}>1 Spotter</span>;
}

const LIBELLE_VERDICT: Record<string, string> = {
  conforme: "Conforme", douteux: "Douteux", non_conforme: "Non conforme",
};

/** Confiance de l'analyse photo en 3 barres : score Vision (0 à 100) s'il
    existe, sinon le verdict ; 0 barre si la photo n'a pas été analysée. */
export function niveauConfiance(verdict?: { verdict?: string; score?: number | null } | null): Niveau {
  if (!verdict?.verdict) return 0;
  if (typeof verdict.score === "number") return verdict.score >= 75 ? 3 : verdict.score >= 40 ? 2 : 1;
  return verdict.verdict === "conforme" ? 3 : verdict.verdict === "douteux" ? 2 : 1;
}

export function AnalysePhoto({ verdict, compact = false }: { verdict?: any; compact?: boolean }) {
  const n = niveauConfiance(verdict);
  if (!verdict?.verdict) {
    return <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>Photo non analysée</Typography>;
  }
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Box sx={{ pt: "3px" }}><TroisBarres niveau={n} taille="s" titre={`Confiance de l'analyse : ${n}/3`} /></Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "var(--c-text-primary)" }}>
          {LIBELLE_VERDICT[verdict.verdict] ?? verdict.verdict}{typeof verdict.score === "number" ? ` · ${verdict.score}/100` : ""}
        </Typography>
        {!compact && verdict.motif && <Typography sx={{ fontSize: 12, color: "var(--c-text-secondary)" }}>{verdict.motif}</Typography>}
        {verdict.erreur && <Typography sx={{ fontSize: 12, color: COULEURS.erreur }}>{verdict.erreur}</Typography>}
      </Box>
    </Box>
  );
}

const etiquette = { color: "var(--c-text-secondary)", fontSize: "11px", fontWeight: 600, mb: 0.25 } as const;

// ═══════════════════════════════════════════════════════════════════════════
// Fiche complète d'un lieu (fenêtre) : même contenu et mêmes actions que la
// fenêtre de validation de l'ancienne page Locations.
// ═══════════════════════════════════════════════════════════════════════════

export function FicheLieuModal({ fiche, onRelance }: { fiche: FicheLieu; onRelance: () => void }) {
  const {
    openLocation, setOpenLocation, images, imagesAvailable, openImgModal, setOpenImgModal,
    formvalues, modalPhotoDistance, modalPhotoConforme, modalPhotoTimestamp, selectedLocationRef, selectedLocation,
    location_write, analyseEnCours, lancerAnalyse, visionInfo, motifs, motifRejet, setMotifRejet,
    commentaireDecision, setCommentaireDecision, decisions, decisionsIndisponibles,
    handleUpdateLocationApproved, handleUpdateLocationRejected,
  } = fiche;
  const [openDialogApprove, setopenDialogApprove] = React.useState(false);
  const [openDialogRejected, setopenDialogRejected] = React.useState(false);
  const handleCloseDialog = () => { setopenDialogApprove(false); setopenDialogRejected(false); };
  const loc: any = selectedLocationRef.current || selectedLocation;

  return (
    <>
      {openLocation && (
        <div
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(31,30,27,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpenLocation(false); }}
        >
          <div role="dialog" aria-label="Fiche du lieu" style={{ background: "var(--c-surface)", border: "1.5px solid #1F1E1B", borderRadius: 8, width: 680, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", padding: 28, position: "relative", color: "var(--c-text-primary)" }}>
            <button
              onClick={() => setOpenLocation(false)}
              aria-label="Fermer"
              style={{ position: "absolute", top: 14, right: 14, background: "transparent", border: "none", cursor: "pointer", color: "var(--c-text-primary)", display: "flex", alignItems: "center" }}
            >
              <CloseIcon />
            </button>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2, pr: 4 }}>
              <TroisBarres taille="s" />
              <Typography sx={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 20 }}>
                {formvalues.locationName.value || "Lieu sans nom"}
              </Typography>
            </Box>

            {/* Photo */}
            {imagesAvailable ? (
              <Box sx={{ mb: 2, borderRadius: "6px", overflow: "hidden", maxHeight: 240, display: "flex", justifyContent: "center", background: "var(--c-surface-3)", border: "1px solid var(--c-border)" }}>
                <img
                  src={`data:image/png;base64,${images[0]}`}
                  alt="Photo du lieu"
                  style={{ maxHeight: 240, maxWidth: "100%", objectFit: "cover", cursor: "zoom-in" }}
                  onClick={() => setOpenImgModal(true)}
                />
              </Box>
            ) : (
              <Box sx={{ mb: 2, height: 120, borderRadius: "6px", background: "var(--c-surface-3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ color: "var(--c-text-secondary)", fontSize: "13px" }}>Aucune image disponible</Typography>
              </Box>
            )}

            {/* Informations */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
              {[
                { label: "Nom", value: formvalues.locationName.value },
                { label: "Type", value: formvalues.locationType.value },
                { label: "Adresse", value: formvalues.address.value },
                { label: "Spotter", value: formvalues.createdBy.value },
                { label: "Tips (découvreur)", value: formvalues.token.value },
              ].map(f => (
                <Box key={f.label}>
                  <Typography sx={etiquette}>{f.label}</Typography>
                  <Typography sx={{ fontSize: "14px" }}>{f.value || "—"}</Typography>
                </Box>
              ))}
              <Box>
                <Typography sx={etiquette}>Proximité photo</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <ProximityBadge distance={modalPhotoDistance} conforme={modalPhotoConforme} />
                  {modalPhotoTimestamp && (
                    <Typography sx={{ color: "var(--c-text-secondary)", fontSize: "11px" }}>
                      {new Date(modalPhotoTimestamp).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Montant fixé par le moteur de pricing à la soumission : le serveur
                ignore toute valeur saisie, d'où un affichage en lecture seule. */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={etiquette}>Tips crédités à l'approbation</Typography>
              <Typography sx={{ fontSize: "14px" }}>
                {selectedLocationRef.current?.aCrediter == null ? "…" : `${selectedLocationRef.current.aCrediter.toLocaleString("fr-FR")} Tips`}
                <span style={{ color: "var(--c-text-secondary)", fontSize: "12px" }}> — calculés par le moteur de pricing (découvreur et corroborateurs non encore payés)</span>
              </Typography>
            </Box>

            {/* Analyse photo et intégrité : aide à la décision */}
            <Box sx={{ mb: 2, p: 1.5, borderRadius: "6px", border: "1px solid var(--c-border)", background: "var(--c-surface-2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 12.5 }}>Analyse photo — aide à la décision</span>
                {location_write && (
                  <BoutonSecondaire size="small" disabled={analyseEnCours} onClick={lancerAnalyse} sx={{ py: 0.25, px: 1.25, fontSize: 12 }}>
                    {analyseEnCours ? "Analyse…" : "Analyser la photo"}
                  </BoutonSecondaire>
                )}
              </div>
              <AnalysePhoto verdict={visionInfo?.verdict} />
              <div style={{ marginTop: 10 }}>
                <span style={{ fontWeight: 600, fontSize: 11.5, color: "var(--c-text-secondary)" }}>Intégrité de la capture</span>
                <div style={{ marginTop: 4 }}>
                  <IntegriteBadges signaux={visionInfo?.integrite} exif={visionInfo?.exif} />
                </div>
              </div>
              {visionInfo?.exif && (() => {
                const e = visionInfo.exif;
                const pill = (couleur: string, t: string) => <span style={pastille(couleur)}>{t}</span>;
                return (
                  <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {pill(e.dateConforme === false ? COULEURS.erreur : e.date ? COULEURS.succes : COULEURS.texteSecondaire,
                      e.date ? (e.dateConforme === false ? `date écart ${e.ecartMinutes} min` : "date OK") : "date absente")}
                    {pill(e.gpsConforme === false ? COULEURS.erreur : e.gpsLatitude ? COULEURS.succes : COULEURS.texteSecondaire,
                      e.gpsLatitude ? (e.gpsConforme === false ? `GPS écart ${e.ecartMetres} m` : "GPS OK") : "GPS absent")}
                    {!e.present && pill(COULEURS.texteSecondaire, "EXIF absent")}
                  </div>
                );
              })()}
            </Box>

            {/* Crédit en échec : lieu approuvé, crédit non confirmé par user-service */}
            {loc?.creditEchec && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: "6px", border: `1px solid ${COULEURS.erreur}`, borderLeft: `6px solid ${COULEURS.erreur}` }}>
                <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 6 }}>Crédit des Tips non confirmé</div>
                <RelanceCredit
                  type="poi"
                  id={loc.referenceId || loc.placekey}
                  message={loc.creditEchecMessage}
                  date={loc.creditEchecLe}
                  peutRelancer={location_write}
                  onRelance={() => { setOpenLocation(false); onRelance(); }}
                />
              </Box>
            )}

            {(loc?.nbCorroborations ?? 0) > 0 && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: "6px", border: "1px solid var(--c-border)", borderLeft: "6px solid var(--d-lieux)" }}>
                <span style={{ fontWeight: 700 }}>{loc.nbCorroborations}</span> corroboration(s) à moins de 20 m (signal, non payées avant approbation).
              </Box>
            )}

            {location_write && (
              <Box sx={{ mb: 2 }}>
                <label htmlFor="fiche-motif" style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Motif de refus</label>
                <select id="fiche-motif" value={motifRejet} onChange={(e) => setMotifRejet(e.target.value)}
                  style={{ width: "100%", padding: "8px", marginBottom: 8 }}>
                  <option value="">— choisir un motif —</option>
                  {motifs.map((m) => <option key={m.code} value={m.code}>{m.libelle}</option>)}
                </select>
                <textarea value={commentaireDecision} onChange={(e) => setCommentaireDecision(e.target.value)}
                  placeholder="Commentaire (obligatoire pour « Autre »)"
                  style={{ width: "100%", padding: "8px", minHeight: 48 }} />
              </Box>
            )}
            {decisionsIndisponibles && (
              <Box sx={{ mb: 2, color: COULEURS.erreur, fontSize: 13 }}>Historique des décisions indisponible (erreur de chargement).</Box>
            )}
            {decisions.length > 0 && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: "6px", border: "1px solid var(--c-border)" }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Historique des décisions</div>
                {decisions.map((d, i) => (
                  <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                    <b>{d.decision}</b> — {d.auteur} — {d.date ? new Date(d.date).toLocaleString() : ""}
                    {d.motifCode ? ` — motif: ${d.motifCode}` : ""}
                    {d.montantCredite ? ` — +${d.montantCredite} Tips` : ""}
                    {d.commentaire ? ` — ${d.commentaire}` : ""}
                  </div>
                ))}
              </Box>
            )}

            {location_write && (
              <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
                <BoutonSecondaire onClick={() => setopenDialogRejected(true)}>Refuser</BoutonSecondaire>
                <BoutonPrincipal onClick={() => setopenDialogApprove(true)} disabled={selectedLocationRef.current?.aCrediter == null}>
                  Valider
                </BoutonPrincipal>
              </Box>
            )}

            <CommonDialog
              open={openDialogApprove}
              handleClose={handleCloseDialog}
              handleConfirm={() => { handleUpdateLocationApproved(); handleCloseDialog(); }}
              contentText={`Approuver ce lieu crédite ${(selectedLocationRef.current?.aCrediter ?? 0)} Tips (découvreur et corroborateurs non encore payés). Le crédit est définitif.`}
              sx={{ zIndex: 10000 }}
            />
            <CommonDialog
              open={openDialogRejected}
              handleClose={handleCloseDialog}
              handleConfirm={() => { handleUpdateLocationRejected(); handleCloseDialog(); }}
              contentText="Rejeter ce lieu ? Le motif choisi est conservé au journal des décisions."
              sx={{ zIndex: 10000 }}
            />
          </div>
        </div>
      )}

      {openImgModal && (
        <div
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(31,30,27,0.9)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setOpenImgModal(false)}
        >
          <InfiniteScroll style={{ maxHeight: "90vh", overflow: "auto" }} dataLength={images.length} next={() => {}} hasMore={false} loader={<h4>Chargement…</h4>}>
            {images.map((img, i) => (
              <img key={i} src={`data:image/png;base64,${img}`} alt={`Photo ${i + 1}`} style={{ maxWidth: "90vw", marginBottom: 8, borderRadius: 6 }} />
            ))}
          </InfiniteScroll>
        </div>
      )}
    </>
  );
}
